import { create } from 'zustand';
import { api } from '@/lib/api';
import { setUser, resetUser, trackEvent } from '@/lib/faro';
import type { AuthResponse, User } from '@/types';

interface AuthState {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (data: {
        display_name: string;
        email: string;
        password: string;
        password_confirmation: string;
        role: string;
    }) => Promise<void>;
    logout: () => Promise<void>;
    fetchUser: () => Promise<void>;
    setAuth: (user: User, token: string) => void;
}

export const useAuth = create<AuthState>((set) => ({
    user: null,
    token: localStorage.getItem('token'),
    isLoading: true,

    setAuth: (user, token) => {
        localStorage.setItem('token', token);
        set({ user, token, isLoading: false });
    },

    login: async (email, password) => {
        const res = await api.post<AuthResponse>('/login', { email, password });
        localStorage.setItem('token', res.token);
        set({ user: res.user, token: res.token, isLoading: false });
        setUser(String(res.user.id), { email: res.user.email, name: res.user.display_name, role: res.user.role });
        trackEvent('user_logged_in', { method: 'email' });
    },

    register: async (data) => {
        const res = await api.post<AuthResponse>('/register', data);
        localStorage.setItem('token', res.token);
        set({ user: res.user, token: res.token, isLoading: false });
        setUser(String(res.user.id), { email: res.user.email, name: res.user.display_name, role: res.user.role });
        trackEvent('user_registered', { role: data.role });
    },

    logout: async () => {
        try {
            await api.post('/logout');
        } catch {
            // ignore
        }
        localStorage.removeItem('token');
        set({ user: null, token: null, isLoading: false });
        trackEvent('user_logged_out');
        resetUser();
    },

    fetchUser: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            set({ isLoading: false });
            return;
        }
        try {
            const res = await api.get<{ data: User }>('/me');
            set({ user: res.data, token, isLoading: false });
            setUser(String(res.data.id), { email: res.data.email, name: res.data.display_name, role: res.data.role });
        } catch {
            localStorage.removeItem('token');
            set({ user: null, token: null, isLoading: false });
        }
    },
}));
